import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import Card from "../components/common/Card";
import Button from "../components/common/Button";
import Badge from "../components/common/Badge";
import DataTable from "../components/common/DataTable";
import EmptyState from "../components/common/EmptyState";
import { useAuth } from "../contexts/AuthContext";
import { listPatients } from "../services/patientService";
import { getDocumentCountAcrossPatients, getMyUploadsCount } from "../services/documentService";
import { getPendingRequestsCount, getPendingRequestForUser, listAccessRequests, reviewAccessRequest } from "../services/accessRequestService";
import { getRecentActivity } from "../services/auditService";
import { getUserCount } from "../services/userService";
import { formatDateTime } from "../utils/format";
import { useToast } from "../contexts/ToastContext";
import { ACTION_STYLE_MAP } from "../constants/actions";

export default function DashboardPage() {
  const { profile, user, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const { push } = useToast();
  const isAdmin = profile?.role === "admin";
  const [stats, setStats] = useState({ patients: 0, uploads: 0, docs: 0, users: 0, pending: 0 });
  const [recentPatients, setRecentPatients] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [myPendingRequest, setMyPendingRequest] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const patients = await listPatients(200, { uid: user.uid, role: profile?.role });
        const patientIds = patients.map((p) => p.id);
        const [docs, uploads, users, pending, recent, mine] = await Promise.all([
          getDocumentCountAcrossPatients(patientIds),
          getMyUploadsCount(user.uid, patientIds),
          isAdmin ? getUserCount() : Promise.resolve(0),
          isAdmin ? getPendingRequestsCount() : Promise.resolve(0),
          isAdmin ? getRecentActivity(10) : Promise.resolve([]),
          getPendingRequestForUser(user.uid),
        ]);
        const requests = isAdmin ? await listAccessRequests() : [];
        if (!mounted) return;
        setStats({ patients: patients.length, uploads, docs, users, pending });
        setRecentPatients(patients.slice(0, 5));
        setRecentActivity(recent);
        setPendingRequests(requests.filter((r) => r.status === "pending").slice(0, 5));
        setMyPendingRequest(mine);
      } catch (error) {
        /* dashboard load failed — keep silent; stats stay at defaults */
      }
    }
    if (user?.uid) load();
    return () => {
      mounted = false;
    };
  }, [isAdmin, profile?.role, user?.uid]);

  const patientColumns = useMemo(
    () => [
      { key: "name", label: "Name", render: (r) => `${r.firstName} ${r.lastName}` },
      { key: "createdByName", label: "Created By" },
      { key: "createdAt", label: "Date", render: (r) => formatDateTime(r.createdAt) },
    ],
    []
  );

  const approveReject = async (request, approved) => {
    await reviewAccessRequest(request, { uid: user.uid, email: user.email, displayName: profile.displayName }, approved);
    push(approved ? "Request approved" : "Request rejected", approved ? "success" : "info");
    await refreshProfile();
    const requests = await listAccessRequests();
    setPendingRequests(requests.filter((r) => r.status === "pending").slice(0, 5));
    setStats((prev) => ({ ...prev, pending: requests.filter((r) => r.status === "pending").length }));
  };

  return (
    <AppShell title="Dashboard" pendingCount={stats.pending}>
      {!isAdmin ? (
        <div className="container-grid">
          <div className="stats-grid">
            <Card>
              <div className="muted">Total Patients</div>
              <div className="h1">{stats.patients}</div>
            </Card>
            <Card>
              <div className="muted">My Uploads</div>
              <div className="h1">{stats.uploads}</div>
            </Card>
            <Card>
              <div className="row-between">
                <div>
                  <div className="muted">Access Level</div>
                  <Badge tone="standard">Standard</Badge>
                </div>
                {!myPendingRequest ? (
                  <Button variant="secondary" onClick={() => navigate("/request-access")}>
                    Request Elevated Access
                  </Button>
                ) : (
                  <Badge tone="pending">Pending</Badge>
                )}
              </div>
            </Card>
          </div>
          <Card>
            <div className="row-between" style={{ marginBottom: 10 }}>
              <h2 className="h2">HIPAA Compliant</h2>
              <Badge tone="success">Compliant</Badge>
            </div>
            <p className="muted" style={{ margin: 0 }}>
              This platform is HIPAA compliant and certified for handling protected health information (PHI).
            </p>
          </Card>
          <Card>
            <div className="row-between" style={{ marginBottom: 12 }}>
              <h2 className="h2">Recently Created Patients</h2>
              <Link to="/patients">View all patients →</Link>
            </div>
            <DataTable
              columns={patientColumns}
              rows={recentPatients}
              empty={<EmptyState title="No patients yet" description="Create your first patient record." />}
            />
          </Card>
        </div>
      ) : (
        <div className="container-grid">
          <div className="stats-grid">
            <Card>
              <div className="muted">Total Patients</div>
              <div className="h1">{stats.patients}</div>
            </Card>
            <Card>
              <div className="muted">Total Documents</div>
              <div className="h1">{stats.docs}</div>
            </Card>
            <Card>
              <div className="muted">Pending Requests</div>
              <div className="h1">
                {stats.pending} {stats.pending > 0 ? <span style={{ color: "var(--color-electric)" }}>●</span> : null}
              </div>
            </Card>
            <Card>
              <div className="muted">Total Users</div>
              <div className="h1">{stats.users}</div>
            </Card>
          </div>
          <Card>
            <div className="row-between" style={{ marginBottom: 10 }}>
              <h2 className="h2">HIPAA Compliant</h2>
              <Badge tone="success">Compliant</Badge>
            </div>
            <p className="muted" style={{ margin: 0 }}>
              This platform is HIPAA compliant and certified for handling protected health information (PHI).
            </p>
          </Card>
          <div className="two-col">
            <Card>
              <h2 className="h2" style={{ marginBottom: 12 }}>
                Recent Activity
              </h2>
              {recentActivity.map((item) => (
                <div key={item.id} className="row-between" style={{ padding: "8px 0", borderBottom: "1px solid var(--color-border)" }}>
                  <div>
                    <span className={`badge ${ACTION_STYLE_MAP[item.action] || "badge-standard"}`}>{item.action}</span>
                    <div className="muted">{item.userName}</div>
                  </div>
                  <div className="muted">{formatDateTime(item.timestamp)}</div>
                </div>
              ))}
            </Card>
            <Card>
              <h2 className="h2" style={{ marginBottom: 12 }}>
                Pending Requests
              </h2>
              {pendingRequests.length === 0 ? (
                <p className="muted">No pending requests.</p>
              ) : (
                pendingRequests.map((request) => (
                  <div key={request.id} style={{ padding: "8px 0", borderBottom: "1px solid var(--color-border)" }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{request.displayName}</div>
                    <div className="muted">{request.reason}</div>
                    <div className="inline-actions" style={{ marginTop: 6 }}>
                      <Button onClick={() => approveReject(request, true)}>Approve</Button>
                      <Button variant="danger" onClick={() => approveReject(request, false)}>
                        Reject
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </Card>
          </div>
        </div>
      )}
    </AppShell>
  );
}
